import { createTheme } from "@mui/material";
import { colorThemes, ColorTheme } from "./colorThemes";
import "@mui/x-data-grid/themeAugmentation";

import { esES } from "@mui/x-data-grid-pro/locales";
import { esES as pickersEsES } from "@mui/x-date-pickers-pro/locales";
import { esES as coreEsES } from "@mui/material/locale";

//https://zenoo.github.io/mui-theme-creator/
//https://mui.com/material-ui/customization/default-theme/
export const createAppTheme = (
	mode: "light" | "dark",
	colorTheme: ColorTheme,
	options: { density?: number; cornerRadius?: number; fontFamily?: string } = {}
) => {

	const { density = 8, cornerRadius = 8, fontFamily = 'Roboto, Helvetica, Arial, sans-serif' } = options;

	const colors = colorThemes[colorTheme];
	return createTheme({
		spacing: density,
		shape: { borderRadius: cornerRadius },
		typography: { fontFamily },
		palette: {
			mode: mode,
			background:
				mode === "light" ? { default: "#ffffff", paper: "#fafafa"} : { default: "#121212", paper: "#1e1e1e", },
			primary: {
				main: colors.primary[500],
				light: colors.primary[200],
				dark: colors.primary[500],
				contrastText: "#000000",
			},
			secondary: {
				main: colors.secondary[500],
				light: colors.secondary[200],
				dark: colors.secondary[500],
				contrastText: "#000000",
			},
			success: {
				main: colors.success[500],
				contrastText: "#eaeaea",
			},
			warning: {
				main: colors.warning[500],
				contrastText: "#eaeaea",
			},
			error: {
				main: colors.error[500],
			},
			info: {
				main: colors.info[500],
			},
			text: mode === "light" ? { primary: "#000", secondary: "#555", } : { primary: "#fff", secondary: "rgba(255, 255, 255, 0.7)", disabled: "rgba(255, 255, 255, 0.5)"},
			contrastThreshold: 3,
			tonalOffset: 0.5,
			action: {
				active: mode === "light" ? "rgba(0, 0, 0, 0.54)" : "#fff",
				hover: mode === "light" ? "rgba(0, 0, 0, 0.04)" : "rgba(255, 255, 255, 0.08)",
				selected: mode === "light" ? "rgba(0, 0, 0, 0.08)" : "rgba(255, 255, 255, 0.16)",
				disabled: mode === "light" ? "rgba(0, 0, 0, 0.26)" : "rgba(255, 255, 255, 0.3)",
				disabledBackground: mode === "light" ? "rgba(0, 0, 0, 0.12)" : "rgba(255, 255, 255, 0.12)",
			},
			divider: mode === "light" ? "rgba(0, 0, 0, 0.12)" : "rgba(255, 255, 255, 0.12)",
		},
		components: {
			MuiAppBar: {
				styleOverrides: {
					root: {
						backgroundColor: mode === "light" ? "#fafafa" : "#1e1e1e",
						color: mode === "light" ? "#1e1e1e" : "#ffffff",
					},
				},
			},
			MuiList: {
				styleOverrides: {
					root: {
						padding: 5
					}
				},
				defaultProps: {
					dense: false,
					disablePadding: false,
				},
				variants: [
					{
						props: { dense: true },
						style: {
							backgroundColor: mode === "light" ? "#ffffff" : "#1e1e1e",
							color: mode === "light" ? "#1e1e1e" : "#ffffff",
						},
					},
				],
			},
			MuiListItemButton: {
				styleOverrides: {
					root: {
						borderLeft: '2px solid transparent',
						borderRadius: cornerRadius,
						'&.Mui-selected': {
							backgroundColor: mode === 'light' ? colors.primary[50] : `${colors.primary[900]}99`,
							borderLeft: `2px solid ${colors.primary[500]}`,
							color: mode === 'light' ? colors.primary[800] : colors.primary[200],
							'& .MuiListItemIcon-root': {
								color: mode === 'light' ? colors.primary[800] : colors.primary[200],
							},
							'&:hover': {
								backgroundColor: mode === 'light' ? colors.primary[100] : `${colors.primary[900]}cc`,
							},
						},
					},
				},
			},
			MuiButton: {
				styleOverrides: {
					root: {
						textTransform: 'capitalize',
						fontSize: '1rem',
						fontWeight: 400,
						lineHeight: 1.5,
						letterSpacing: '0.00938em',
						color: "#fff",
						'&:hover': {
							backgroundColor: mode === "light" ? colors.primary[50] : `${colors.primary[900]}99`,
						},

					},
					containedPrimary: {
						backgroundColor: colors.primary[500],
						color: "#fff",
						'&:hover': {
							backgroundColor: colors.primary[600],
						},
					},
					textPrimary: {
						color: colors.primary[500],
						'&:hover': {
							backgroundColor: colors.primary[50],
						},
					},
					outlinedPrimary: {
						borderColor: colors.primary[500],
						color: colors.primary[500],
						'&:hover': {
							borderColor: colors.primary[700],
							backgroundColor: colors.primary[50],
						},
					},
					outlined: {
						borderColor: mode === "light" ? "rgba(0, 0, 0, 0.23)" : "rgba(255, 255, 255, 0.23)",
						color: mode === "light" ? "#000" : "#fff",
						'&:hover': {
							borderColor: mode === "light" ? "rgba(0, 0, 0, 0.23)" : "rgba(255, 255, 255, 0.23)",
							backgroundColor: mode === "light" ? "rgba(0, 0, 0, 0.04)" : "rgba(255, 255, 255, 0.08)",
						},
						'&.Mui-disabled': {
							borderColor: mode === "light" ? "rgba(0, 0, 0, 0.12)" : "rgba(255, 255, 255, 0.12)",
							color: mode === "light" ? "rgba(0, 0, 0, 0.26)" : "rgba(255, 255, 255, 0.3)",
						},
						'&.Mui-disabled:hover': {
							borderColor: mode === "light" ? "rgba(0, 0, 0, 0.12)" : "rgba(255, 255, 255, 0.12)",
							color: mode === "light" ? "rgba(0, 0, 0, 0.26)" : "rgba(255, 255, 255, 0.3)",
							backgroundColor: 'transparent',
						},
						'&.Mui-disabled:active': {
							borderColor: mode === "light" ? "rgba(0, 0, 0, 0.12)" : "rgba(255, 255, 255, 0.12)",
							color: mode === "light" ? "rgba(0, 0, 0, 0.26)" : "rgba(255, 255, 255, 0.3)",
							backgroundColor: 'transparent',
						},
						'&.Mui-disabled:focus': {
							borderColor: mode === "light" ? "rgba(0, 0, 0, 0.12)" : "rgba(255, 255, 255, 0.12)",
							color: mode === "light" ? "rgba(0, 0, 0, 0.26)" : "rgba(255, 255, 255, 0.3)",
							backgroundColor: 'transparent',
						},
						'&.Mui-disabled.Mui-disabled': {
							borderColor: mode === "light" ? "rgba(0, 0, 0, 0.12)" : "rgba(255, 255, 255, 0.12)",
							color: mode === "light" ? "rgba(0, 0, 0, 0.26)" : "rgba(255, 255, 255, 0.3)",
							backgroundColor: 'transparent',
						},
						'&.Mui-disabled.Mui-disabled:hover': {
							borderColor: mode === "light" ? "rgba(0, 0, 0, 0.12)" : "rgba(255, 255, 255, 0.12)",
							color: mode === "light" ? "rgba(0, 0, 0, 0.26)" : "rgba(255, 255, 255, 0.3)",
							backgroundColor: 'transparent',
						},
						'&.Mui-disabled.Mui-disabled:active': {
							borderColor: mode === "light" ? "rgba(0, 0, 0, 0.12)" : "rgba(255, 255, 255, 0.12)",
							color: mode === "light" ? "rgba(0, 0, 0, 0.26)" : "rgba(255, 255, 255, 0.3)",
							backgroundColor: 'transparent',
						},
						'&.Mui-disabled.Mui-disabled:focus': {
							borderColor: mode === "light" ? "rgba(0, 0, 0, 0.12)" : "rgba(255, 255, 255, 0.12)",
							color: mode === "light" ? "rgba(0, 0, 0, 0.26)" : "rgba(255, 255, 255, 0.3)",
							backgroundColor: 'transparent',
						},
						'&.Mui-disabled.Mui-disabled.Mui-disabled': {
							borderColor: mode === "light" ? "rgba(0, 0, 0, 0.12)" : "rgba(255, 255, 255, 0.12)",
							color: mode === "light" ? "rgba(0, 0, 0, 0.26)" : "rgba(255, 255, 255, 0.3)",
							backgroundColor: 'transparent',
						},
						'&.Mui-disabled.Mui-disabled.Mui-disabled:hover': {
							borderColor: mode === "light" ? "rgba(0, 0, 0, 0.12)" : "rgba(255, 255, 255, 0.12)",
							color: mode === "light" ? "rgba(0, 0, 0, 0.26)" : "rgba(255, 255, 255, 0.3)",
							backgroundColor: 'transparent',
						},
						'&.Mui-disabled.Mui-disabled.Mui-disabled:active': {
							borderColor: mode === "light" ? "rgba(0, 0, 0, 0.12)" : "rgba(255, 255, 255, 0.12)",
							color: mode === "light" ? "rgba(0, 0, 0, 0.26)" : "rgba(255, 255, 255, 0.3)",
							backgroundColor: 'transparent',
						},
						'&.Mui-disabled.Mui-disabled.Mui-disabled:focus': {
							borderColor: mode === "light" ? "rgba(0, 0, 0, 0.12)" : "rgba(255, 255, 255, 0.12)",
							color: mode === "light" ? "rgba(0, 0, 0, 0.26)" : "rgba(255, 255, 255, 0.3)",
							backgroundColor: 'transparent',
						},
						'&.Mui-disabled.Mui-disabled.Mui-disabled.Mui-disabled': {
							borderColor: mode === "light" ? "rgba(0, 0, 0, 0.12)" : "rgba(255, 255, 255, 0.12)",
							color: mode === "light" ? "rgba(0, 0, 0, 0.26)" : "rgba(255, 255, 255, 0.3)",
							backgroundColor: 'transparent',
						},
					},
					containedSecondary: {
						backgroundColor: colors.secondary[500],
						color: "#fff",
						'&:hover': {
							backgroundColor: colors.secondary[600],
						},
					},
					containedError: {
						//backgroundColor: colors.error[500],
						color: mode === "light" ? "#eaeaea" : "#121212",
						/* '&:hover': {
							backgroundColor: colors.error[600],
						}, */
						backgroundColor: mode === "light" ? '#121212' : '#eaeaea',
						'&:hover': {
							backgroundColor: mode === "light" ? '#1e1e1e' : '#eaeaea',
						},
					},
					textInherit: {
						color: 'inherit',
					},
				},
			},
			MuiTableContainer: {
				styleOverrides: {
					root: {
						backgroundColor: mode === "light" ? "#fafafa" : "#1e1e1e",
						color: mode === "light" ? "#1e1e1e" : "#ffffff",
					},
				},
			},
			MuiTable: {
				styleOverrides: {
					root: {
						backgroundColor: mode === "light" ? "#fafafa" : "#1e1e1e",
						color: mode === "light" ? "#1e1e1e" : "#ffffff",
					},
				},
			},
			MuiTableHead: {
				styleOverrides: {
					root: {
						backgroundColor: mode === "light" ? "transparent" : "#1e1e1e",
						color: mode === "light" ? "#1e1e1e" : "#ffffff",
					},
				},
			},
			MuiTableBody: {
				styleOverrides: {
					root: {
						backgroundColor: mode === "light" ? "#fafafa" : "#1e1e1e",
						color: mode === "light" ? "#1e1e1e" : "#ffffff",
					},
				},
			},
			MuiTableCell: {
				styleOverrides: {
					root: {
						backgroundColor: mode === "light" ? "#fafafa" : "#1e1e1e",
						color: mode === "light" ? "#1e1e1e" : "#ffffff",
					},
				},
			},
			MuiDataGrid: {
				styleOverrides: {
					root: {
						backgroundColor: mode === "light" ? "#fafafa" : "#1e1e1e",
						"& .MuiDataGrid-columnHeaders": {
							backgroundColor: mode === "light" ? "#fafafa" : "#1e1e1e",
						},
						"& .MuiDataGrid-columnHeader": {
							backgroundColor: mode === "light" ? "#fafafa" : "#1e1e1e",
						},
						"& .MuiDataGrid-columnHeaderRow": {
							backgroundColor: mode === "light" ? "#fafafa" : "#1e1e1e",
						},
						"& .MuiDataGrid-columnHeaderRow--filter": {
							backgroundColor: mode === "light" ? "#fafafa" : "#1e1e1e",
						},
						"& .MuiDataGrid-footerContainer": {
							backgroundColor: mode === "light" ? "#fafafa" : "#1e1e1e",
						},
						"& .MuiDataGrid-inputBase-root": {
							backgroundColor: mode === "light" ? "#fafafa" : "#1e1e1e",
						},
						"& .MuiDataGrid-columnHeader .MuiInputBase-root, & .MuiDataGrid-columnHeaderFilterInput .MuiInputBase-root": {
							//backgroundColor: mode === "light" ? "transparent" : "#1e1e1e",
							color: mode === "light" ? "#1e1e1e" : "#ffffff",
						},
						"& .MuiDataGrid-columnHeader .MuiInputBase-input, & .MuiDataGrid-columnHeaderFilterInput .MuiInputBase-input & .MuiInput-input": {
							//backgroundColor: mode === "light" ? "transparent" : "#1e1e1e",
							color: mode === "light" ? "#1e1e1e" : "#ffffff",

						},
						"& .MuiDataGrid-columnHeader .MuiFormControl-root": {
							backgroundColor: mode === "light" ? "transparent" : "#1e1e1e",
							color: mode === "light" ? "#1e1e1e" : "#ffffff",
						},
						"& .MuiDataGrid-columnHeader .MuiSelect-select": {
							backgroundColor: mode === "light" ? "#fafafa" : "#1e1e1e",
							color: mode === "light" ? "#1e1e1e" : "#ffffff",
						},
						"& .MuiDataGrid-root .MuiDataGrid-columnHeader .MuiInputBase-root": {
							backgroundColor: mode === "light" ? "transparent" : "#1e1e1e",
							color: mode === "light" ? "#1e1e1e" : "#ffffff",
						},
						"& .MuiDataGrid-overlay": {
							backgroundColor: mode === "light" ? "#fafafa" : "#1e1e1e",
						},
					},
				},
			},
			MuiAlert: {
				styleOverrides: {
					root: {
						backgroundColor: mode === "light" ? "#ffffff" : "#1e1e1e",
						color: mode === "light" ? "#1e1e1e" : "#ffffff",
					},
				},
				defaultProps: {
					variant: 'filled',
				},
				variants: [
					{
						props: { severity: 'success' },
						style: {
							backgroundColor: colors.success[500],
							color: "#fff",
						},
					},
					{
						props: { severity: 'error' },
						style: {
							backgroundColor: colors.error[500],
							color: "#fff",
						},
					},
					{
						props: { severity: 'warning' },
						style: {
							backgroundColor: colors.warning[500],
							color: "#1e1e1e",
						},
					},
					{
						props: { severity: 'info' },
						style: {
							backgroundColor: colors.info[500],
							color: "#fff",
						},
					},
				],
			},
			MuiDialog: {
				styleOverrides: {
					paper: {
						backgroundColor: mode === "light" ? "#ffffff" : "#1e1e1e",
						color: mode === "light" ? "#1e1e1e" : "#ffffff",
						backgroundImage: 'none',
					},
					paperWidthLg: {
						backgroundColor: mode === "light" ? "#ffffff" : "#1e1e1e",
						color: mode === "light" ? "#1e1e1e" : "#ffffff",
						border: `1px solid ${mode === "light" ? "rgba(0, 0, 0, 0.12)" : "rgba(255, 255, 255, 0.12)"}`,
					},
					paperWidthMd: {
						backgroundColor: mode === "light" ? "#ffffff" : "#1e1e1e",
						color: mode === "light" ? "#1e1e1e" : "#ffffff",
						//backgroundImage: 'none',
						border: `1px solid ${mode === "light" ? "rgba(0, 0, 0, 0.12)" : "rgba(255, 255, 255, 0.12)"}`,
					},
					paperWidthSm: {
						backgroundColor: mode === "light" ? "#ffffff" : "#1e1e1e",
						color: mode === "light" ? "#1e1e1e" : "#ffffff",
						border: `1px solid ${mode === "light" ? "rgba(0, 0, 0, 0.12)" : "rgba(255, 255, 255, 0.12)"}`,
						// backgroundColor: mode === "light" ? "#ffffff" : "#1e1e1e",
						// color: mode === "light" ? "#1e1e1e" : "#eaeaea",
					}
				}
			},
			MuiTextField: {
				styleOverrides: {
					root: {
						backgroundColor: mode === "light" ? "#fafafa" : "#1e1e1e",
						color: mode === "light" ? "#1e1e1e" : "#ffffff",
					},
				},
				defaultProps: {
					variant: 'outlined',

				},
				variants: [
					{
						props: { variant: 'outlined' },
						style: {
							backgroundColor: mode === "light" ? "#fafafa" : "#1e1e1e",
							color: mode === "light" ? "#1e1e1e" : "#ffffff",
						},
					},
					{
						props: { variant: 'filled' },
						style: {
							backgroundColor: mode === "light" ? "#fafafa" : "#1e1e1e",
							color: mode === "light" ? "#1e1e1e" : "#ffffff",
						},
					},
					{
						props: { variant: 'standard' },
						style: {
							backgroundColor: mode === "light" ? "#fafafa" : "#1e1e1e",
							color: mode === "light" ? "#1e1e1e" : "#ffffff",
						},
					},
				]
			},
			MuiInputBase: {
				styleOverrides: {
					root: {
						//backgroundColor: mode === "light" ? "#fafafa" : "#252525",
						color: mode === "light" ? "#1e1e1e" : "#ffffff",
					},
					input: {
						//backgroundColor: mode === "light" ? "#fafafa" : "#252525",
						color: mode === "light" ? "#1e1e1e" : "#ffffff",
						"&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus, &:-webkit-autofill:active": {
							WebkitBoxShadow: mode === "light" ? "#fafafa" : "0 0 0 100px #252525 inset",
							//boxShadow: mode === "light" ? "#fafafa" : "0 0 0 100px #252525 inset",
							WebkitTextFillColor: mode === "light" ? "#fafafa" : "#1e1e1e",
							caretColor: mode === "light" ? "#1e1e1e" : "#ffffff",
						},
						borderRadius: cornerRadius,
					}

				},
			},
			MuiFormControl: {
				styleOverrides: {
					root: {
						//backgroundColor: mode === "light" ? "#fafafa" : "#1e1e1e",
						color: mode === "light" ? "#1e1e1e" : "#ffffff",
						borderRadius: cornerRadius,
						'& .MuiInputBase-root': {
							//backgroundColor: mode === "light" ? "#fafafa" : "#1e1e1e",
							color: mode === "light" ? "#1e1e1e" : "#ffffff",
						},
						'& .MuiInputBase-input': {
							//backgroundColor: mode === "light" ? "#fafafa" : "#1e1e1e",
							color: mode === "light" ? "#1e1e1e" : "#ffffff",
							"&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus, &:-webkit-autofill:active": {
								WebkitBoxShadow: mode === "light" ? "#fafafa" : "0 0 0 100px #252525 inset",
								boxShadow: mode === "light" ? "#fafafa" : "0 0 0 100px #252525 inset",
								WebkitTextFillColor: mode === "light" ? "#1e1e1e" : "#ffffff",
								caretColor: mode === "light" ? "#1e1e1e" : "#ffffff",
							},
						},
						'& .MuiInputLabel-root': {
							color: mode === "light" ? "#1e1e1e" : "#ffffff",
						}
					},
				},
				defaultProps: {
					variant: 'outlined',
				},
				variants: [
					{
						props: { variant: 'outlined' },
						style: {
							backgroundColor: mode === "light" ? "#fafafa" : "#1e1e1e",
							color: mode === "light" ? "#1e1e1e" : "#ffffff",
						},
					},
					{
						props: { variant: 'filled' },
						style: {
							backgroundColor: mode === "light" ? "#fafafa" : "#1e1e1e",
							color: mode === "light" ? "#1e1e1e" : "#ffffff",
						},
					},
					{
						props: { variant: 'standard' },
						style: {
							backgroundColor: mode === "light" ? "#fafafa" : "#1e1e1e",
							color: mode === "light" ? "#1e1e1e" : "#ffffff",
						},
					},
				]
			},
			MuiOutlinedInput: {
				styleOverrides: {
					root: {
						backgroundColor: mode === "light" ? "#fafafa" : "#252525",
						color: mode === "light" ? "#1e1e1e" : "#ffffff",
					},
					input: {
						backgroundColor: mode === "light" ? "#fafafa" : "#252525",
						color: mode === "light" ? "#1e1e1e" : "#ffffff",
						"&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus, &:-webkit-autofill:active": {
							WebkitBoxShadow: mode === "light" ? "#fafafa" : "0 0 0 100px #252525 inset",
							//boxShadow: mode === "light" ? "#fafafa" : "0 0 0 100px #252525 inset",
							WebkitTextFillColor: mode === "light" ? "#1e1e1e" : "#ffffff",
							caretColor: mode === "light" ? "#1e1e1e" : "#ffffff",
						}
					}
				},
			},
			MuiSelect: {
				styleOverrides: {
					root: {
						backgroundColor: mode === "light" ? "#eaeaea" : "#252525",
						color: mode === "light" ? "#1e1e1e" : "#ffffff",
					},
				},
				defaultProps: {
					variant: 'outlined',
				},
				variants: [
					{
						props: { variant: 'outlined' },
						style: {
							backgroundColor: mode === "light" ? "#eaeaea" : "#252525",
							color: mode === "light" ? "#1e1e1e" : "#ffffff",
						},
					},
					{
						props: { variant: 'filled' },
						style: {
							backgroundColor: mode === "light" ? "#f5f5f5" : "#252525",
							color: mode === "light" ? "#1e1e1e" : "#ffffff",
						},
					},
					{
						props: { variant: 'standard' },
						style: {
							backgroundColor: mode === "light" ? "#f5f5f5" : "#252525",
							color: mode === "light" ? "#1e1e1e" : "#ffffff",
						},
					},
				]
			},
			MuiCheckbox: {
				styleOverrides: {
					root: {
						color: colors.primary[300],
					},
				},
				defaultProps: {
					color: 'primary',
				},
			}
		},

	},
		esES,
		pickersEsES,
		coreEsES // core translations
	);
}